import React, { useMemo } from 'react'
import { ApiClientContext, type PostStreamOptions } from '@paintscout/react-quote'
import { getAuthToken } from 'shared/graphql/client'
import http from '@paintscout/util/http'

const resolveUrl = (path: string, baseUrl?: string): string => {
  const base = (baseUrl || process.env.PS_API_URL || '').replace(/\/$/, '')
  if (!path) return base
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`
}

export function ApiClientProvider({ children }: { children: React.ReactNode }) {
  const client = useMemo(
    () => ({
      get: async <T = any,>(path: string): Promise<T> => {
        const token = await getAuthToken()
        const { data } = await http.get<T>(resolveUrl(path), {
          headers: { Authorization: `Bearer ${token}` }
        })
        return data
      },
      post: async <T = any,>(path: string, body?: unknown): Promise<T> => {
        const token = await getAuthToken()
        const { data } = await http.post<T>(resolveUrl(path), body, {
          headers: { Authorization: `Bearer ${token}` }
        })
        return data
      },
      postStream: async (path: string, body: unknown, opts: PostStreamOptions): Promise<void> => {
        const token = await getAuthToken()
        const url = resolveUrl(path, opts.baseUrl)
        const requestUrl = url.includes('?') ? `${url}&stream=true` : `${url}?stream=true`
        const response = await fetch(requestUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body),
          signal: opts.signal
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(errorText || `Request failed with status ${response.status}`)
        }

        const contentType = response.headers.get('content-type') || ''
        const isSSE = contentType.includes('text/event-stream')

        if (!isSSE) {
          // Backend returned a plain JSON response (non-streaming mode).
          // Parse it directly and emit a synthetic 'done' event.
          const body = await response.json()
          opts.onEvent('done', body)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('Streaming response body is not available')
        }

        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        let readerDone = false
        while (!readerDone) {
          const { value, done } = await reader.read()
          if (done) {
            readerDone = true
            break
          }
          buffer += decoder.decode(value, { stream: true })
          let idx = buffer.indexOf('\n\n')
          while (idx !== -1) {
            const chunk = buffer.slice(0, idx)
            buffer = buffer.slice(idx + 2)
            const lines = chunk.split('\n').map((line) => line.trim())
            let eventName = ''
            const dataLines: string[] = []
            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventName = line.slice('event:'.length).trim()
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice('data:'.length).trim())
              }
            }
            const dataRaw = dataLines.join('\n')
            let data: any = dataRaw
            try {
              data = JSON.parse(dataRaw)
            } catch {
              // keep raw string
            }
            if (eventName) {
              opts.onEvent(eventName, data)
            }
            idx = buffer.indexOf('\n\n')
          }
        }
      }
    }),
    []
  )

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>
}

export default ApiClientProvider
